import { MutationCache, QueryClient } from "@tanstack/react-query";

/**
 * De queryclient van de applicatie, met één afspraak erin: <b>gelukt schrijven betekent verversen</b>.
 *
 * <p>Zonder die afspraak blijft het scherm tonen wat de database niet meer zegt. Boek je een dier uit,
 * dan staat het er nog gewoon aanwezig bij; lees je een enting uit het boekje, dan zit de foto wel bij
 * Media en documenten maar zie je hem niet. Dat werd tot nu toe per scherm opgelost met een losse
 * {@code useInvalidate} — op vijf van de tientallen schermen, en dus overal elders niet.
 *
 * <p>Daarom hangt het hier aan de mutatiecache: elke geslaagde mutatie, waar in de applicatie ook,
 * markeert alles als verouderd. React Query haalt dan alleen de queries opnieuw op die op dat moment
 * in beeld staan; de rest wordt pas ververst als je er weer naartoe gaat. De prijs is dus één extra
 * ophaalronde voor het scherm waar je op staat, en dat is precies het scherm dat moest bijwerken.
 *
 * <p>De twee standaardinstellingen zijn die van Refine zelf, die we hier overnemen omdat een eigen
 * client de zijne vervangt: niet verversen als het venster focus krijgt, en bij het omslaan van een
 * pagina de vorige gegevens laten staan tot de nieuwe binnen zijn.
 *
 * <p><b>Twee uitzonderingen, allebei om het inloggen heen</b> (Maarten, 2026-10-12: "knoppen die
 * flikkeren"):
 * <ul>
 *   <li>De inlogcontrole zelf wordt nooit mee ververst. {@code <Authenticated>} haalt het hele scherm
 *     weg zolang die controle loopt, dus elke opslag liet de applicatie een tel verdwijnen en gooide
 *     de invoer op het scherm weg.</li>
 *   <li>Refines eigen auth-mutaties tellen niet als schrijven. De belangrijkste is {@code useCheckError}:
 *     die draait na élk mislukt verzoek. Een verzoek dat hoort te mislukken (de AI-peiling op Asiel
 *     tarieven krijgt een 405) verversde zo alles, mislukte daardoor opnieuw, en bleef in een lus het
 *     scherm laten knipperen: elf keer in vier seconden.</li>
 * </ul>
 */

/** De eerste sleutel van Refines auth-queries en -mutaties, in de oude en de nieuwe naamgeving. */
const AUTH_SLEUTELS = new Set([
  "auth", "useAuthenticated", "usePermissions", "getUserIdentity",
  "useCheckError", "useLogin", "useLogout", "useRegister", "useForgotPassword", "useUpdatePassword",
]);

/** Of deze query- of mutatiesleutel van het inloggen is, en dus buiten "schrijven is verversen" valt. */
export function isAuthSleutel(sleutel: unknown): boolean {
  return Array.isArray(sleutel) && typeof sleutel[0] === "string" && AUTH_SLEUTELS.has(sleutel[0]);
}

export function maakQueryClient(): QueryClient {
  let client!: QueryClient;
  const mutationCache = new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (isAuthSleutel(mutation.options.mutationKey)) {
        return;
      }
      void client.invalidateQueries({ predicate: (query) => !isAuthSleutel(query.queryKey) });
    },
  });
  client = new QueryClient({
    mutationCache,
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
      },
    },
  });
  return client;
}
